import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { copyToClipboard } from "@/lib/clipboard";
import { QrCode, Download, Share2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact QR popover button.
 * Props:
 *   - url: the URL to encode
 *   - filename: download file name (defaults to "payment-link.png")
 *   - size: button size (matches shadcn Button — "sm" | "icon" | "default")
 *   - variant: button variant (default "ghost")
 *   - label: optional label shown inside the button
 */
export default function QrButton({
  url,
  filename = "payment-link.png",
  size = "sm",
  variant = "ghost",
  label,
  title = "Show QR",
  testId,
}) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!url) return null;

  const downloadPng = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return toast.error("QR not ready");
    canvas.toBlob((blob) => {
      if (!blob) return toast.error("Download failed");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("QR saved");
    }, "image/png");
  };

  const sharePng = async () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return toast.error("QR not ready");
    setBusy(true);
    try {
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("Could not render");
      const file = new File([blob], filename, { type: "image/png" });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Payment link",
          text: url,
        });
        return;
      }
      // No native share — fall back to copying the URL
      await copyToClipboard(url, { successMessage: "Link copied — share it from your apps" });
    } catch {
      // user cancelled
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={size}
          title={title}
          data-testid={testId || "qr-button"}
          className={label ? "" : "px-2"}
        >
          <QrCode className={`h-3.5 w-3.5 ${label ? "mr-1.5" : ""}`} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="end">
        <div className="flex flex-col items-center gap-3">
          <div ref={canvasRef} className="rounded-lg bg-white p-3 border border-stone-200">
            <QRCodeCanvas
              value={url}
              size={200}
              level="M"
              fgColor="#0B1B3D"
              bgColor="#FFFFFF"
              includeMargin={false}
            />
          </div>
          <p className="text-[11px] text-stone-500 text-center max-w-[220px] break-all">
            {url}
          </p>
          <div className="flex gap-2 w-full">
            <Button
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(url, { successMessage: "URL copied" })}
              className="flex-1"
              data-testid="qr-copy-url"
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={downloadPng}
              className="flex-1"
              data-testid="qr-download"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Save
            </Button>
            <Button
              size="sm"
              onClick={sharePng}
              disabled={busy}
              className="flex-1 bg-[#1340A6] hover:bg-[#0F3590] text-white"
              data-testid="qr-share"
            >
              {busy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Share2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Share
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
